import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserPlus,
  UserRoundX,
  FileText,
  CheckCircle2,
  Clock,
  Ban,
  BarChart3,
  Stethoscope,
  RefreshCw,
} from "lucide-react";
import { getRecentAuditLogs } from "../../services/auditService";
import { getAllMDTReports } from "../../services/mdtReportService";
import { getAllDoctors } from "../../services/doctorService";
import MDTDonut from "./MDTDonut";
import OnboardingLineChart from "./OnboardingLineChart";
import OverviewStatStrip from "./OverviewStatStrip";
import {
  TableSkeleton,
  OverviewCardSkeleton,
} from "../Common/Skeleton";
import WidgetErrorState from "../Common/WidgetErrorState";
import EmptyState from "../Common/EmptyState";
import StaleDataBanner from "../Common/StaleDataBanner";
import { handleApiError } from "../../utils/errorHandler";

const timeAgo = (dateString: string) => {
  const now = new Date().getTime();
  const past = new Date(dateString).getTime();
  const diff = Math.floor((now - past) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hour ago`;
  return `${Math.floor(diff / 86400)} day ago`;
};

/**
 * Dedicated dashboard for Super Admin only.
 * Uses admin-specific APIs with independent error isolation, loading states, and retry support:
 * - /audit/recent
 * - /mdt-reports
 * - /doctors
 */
const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  // 1. Doctors state
  const [allDoctors, setAllDoctors] = useState<any[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState<boolean>(true);
  const [errorDoctors, setErrorDoctors] = useState<string | null>(null);
  const [isStaleDoctors, setIsStaleDoctors] = useState<boolean>(false);
  const doctorsRequestIdRef = useRef<number>(0);

  // 2. MDTs state
  const [allReports, setAllReports] = useState<any[]>([]);
  const [submittedMdts, setSubmittedMdts] = useState<any[]>([]);
  const [loadingMdts, setLoadingMdts] = useState<boolean>(true);
  const [errorMdts, setErrorMdts] = useState<string | null>(null);
  const [isStaleMdts, setIsStaleMdts] = useState<boolean>(false);
  const mdtsRequestIdRef = useRef<number>(0);

  // 3. Activity state
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loadingActivity, setLoadingActivity] = useState<boolean>(true);
  const [errorActivity, setErrorActivity] = useState<string | null>(null);
  const [isStaleActivity, setIsStaleActivity] = useState<boolean>(false);
  const activityRequestIdRef = useRef<number>(0);

  const mapAuditToActivity = (log: any) => {
    const rawTitle = log.title || "";
    const title = rawTitle.toLowerCase();
    const subtitle = [log.actorName, log.targetName].filter(Boolean).join(" • ");

    let type:
      | "patient"
      | "report"
      | "doctor-blocked"
      | "doctor-unblocked"
      | "user"
      | "note" = "note";

    if (log.entity === "DOCTOR" && title.includes("status changed to inactive")) {
      type = "doctor-blocked";
    } else if (log.entity === "DOCTOR" && title.includes("status changed to active")) {
      type = "doctor-unblocked";
    } else if (title.includes("patient")) {
      type = "patient";
    } else if (
      title.includes("imaging") ||
      title.includes("histology") ||
      title.includes("clinical") ||
      title.includes("mdt") ||
      title.includes("report")
    ) {
      type = "report";
    } else if (title.includes("doctor") || title.includes("user")) {
      type = "user";
    }

    return {
      id: log.id,
      action: rawTitle,
      subtitle,
      createdAt: log.createdAt,
      type,
    };
  };

  // Fetch Doctors
  const fetchDoctors = useCallback(async () => {
    const requestId = ++doctorsRequestIdRef.current;
    setLoadingDoctors(true);

    try {
      const doctors = await getAllDoctors();
      if (requestId !== doctorsRequestIdRef.current) return;
      const doctorsArray = Array.isArray(doctors) ? doctors : Object.values(doctors ?? {});
      setAllDoctors(doctorsArray);
      setErrorDoctors(null);
      setIsStaleDoctors(false);
    } catch (err: unknown) {
      if (requestId !== doctorsRequestIdRef.current) return;
      const apiErr = handleApiError(err);
      setErrorDoctors(apiErr.message || "Failed to load doctor records");
      if (allDoctors.length > 0) {
        setIsStaleDoctors(true);
      }
    } finally {
      if (requestId === doctorsRequestIdRef.current) {
        setLoadingDoctors(false);
      }
    }
  }, [allDoctors.length]);

  // Fetch MDT Reports
  const fetchMdts = useCallback(async () => {
    const requestId = ++mdtsRequestIdRef.current;
    setLoadingMdts(true);

    try {
      const reports = await getAllMDTReports();
      if (requestId !== mdtsRequestIdRef.current) return;
      const reportsArray = Array.isArray(reports) ? reports : Object.values(reports ?? {});
      setAllReports(reportsArray);
      const submitted = reportsArray
        .filter((r: any) => ["approved", "final", "generated", "validated"].includes(r.status))
        .sort(
          (a: any, b: any) =>
            new Date(b.updatedAt ?? b.createdAt).getTime() -
            new Date(a.updatedAt ?? a.createdAt).getTime()
        )
        .slice(0, 5);
      setSubmittedMdts(submitted);
      setErrorMdts(null);
      setIsStaleMdts(false);
    } catch (err: unknown) {
      if (requestId !== mdtsRequestIdRef.current) return;
      const apiErr = handleApiError(err);
      setErrorMdts(apiErr.message || "Failed to load MDT reports");
      if (allReports.length > 0) {
        setIsStaleMdts(true);
      }
    } finally {
      if (requestId === mdtsRequestIdRef.current) {
        setLoadingMdts(false);
      }
    }
  }, [allReports.length]);

  // Fetch Recent Activity
  const fetchActivity = useCallback(async () => {
    const requestId = ++activityRequestIdRef.current;
    setLoadingActivity(true);

    try {
      const logs = await getRecentAuditLogs();
      if (requestId !== activityRequestIdRef.current) return;
      const logsArray = Array.isArray(logs) ? logs : Object.values(logs ?? {});
      setRecentActivity(logsArray.slice(0, 7).map(mapAuditToActivity));
      setErrorActivity(null);
      setIsStaleActivity(false);
    } catch (err: unknown) {
      if (requestId !== activityRequestIdRef.current) return;
      const apiErr = handleApiError(err);
      setErrorActivity(apiErr.message || "Failed to load recent activity");
      if (recentActivity.length > 0) {
        setIsStaleActivity(true);
      }
    } finally {
      if (requestId === activityRequestIdRef.current) {
        setLoadingActivity(false);
      }
    }
  }, [recentActivity.length]);

  useEffect(() => {
    fetchDoctors();
    fetchMdts();
    fetchActivity();
  }, [fetchDoctors, fetchMdts, fetchActivity]);

  const isAnyLoading = loadingDoctors || loadingMdts || loadingActivity;

  /* ---- MDT Overview donut ---- */
  const donutData = useMemo(() => {
    return {
      approved: allReports.filter((r: any) => ["approved", "final"].includes(r.status)).length,
      pending: allReports.filter((r: any) =>
        ["generated", "validated", "draft"].includes(r.status)
      ).length,
      rejected: allReports.filter((r: any) => r.status === "rejected").length,
      total: allReports.length,
    };
  }, [allReports]);

  const donutSegments = useMemo(
    () => [
      { label: "Approved", value: donutData.approved, color: "#15BBAE" },
      { label: "Pending", value: donutData.pending, color: "#D9E0E7" },
      { label: "Rejected", value: donutData.rejected, color: "#F45B78" },
    ],
    [donutData]
  );
  const totalMdts = donutData.total;

  /* ---- Doctor Onboarding Overview donut ---- */
  const onboardingCounts = useMemo(() => {
    return {
      completed: allDoctors.filter((d: any) => d.onboardingStatus === "completed").length,
      inProgress: allDoctors.filter((d: any) => d.onboardingStatus === "in progress").length,
      notStarted: allDoctors.filter(
        (d: any) => !["completed", "in progress"].includes(d.onboardingStatus)
      ).length,
      total: allDoctors.length,
    };
  }, [allDoctors]);

  const doctorSegments = useMemo(
    () => [
      { label: "Completed", value: onboardingCounts.completed, color: "#15BBAE" },
      { label: "In Progress", value: onboardingCounts.inProgress, color: "#D9E0E7" },
      { label: "Not Started", value: onboardingCounts.notStarted, color: "#F45B78" },
    ],
    [onboardingCounts]
  );
  const totalDoctors = onboardingCounts.total;

  /* Timestamps for dynamic line charts */
  const doctorDates = useMemo(() => {
    return allDoctors.map((d: any) => d.createdAt ?? d.created_at ?? d.onboardingDate).filter(Boolean);
  }, [allDoctors]);

  const mdtDates = useMemo(() => {
    return allReports.map((r: any) => r.createdAt ?? r.updatedAt ?? r.updated_at ?? r.submitted_at).filter(Boolean);
  }, [allReports]);

  const mdtTitle = (mdt: any) => {
    if (!mdt) return "MDT Report";
    if (mdt.patient_name || mdt.patientName) return `${mdt.patient_name || mdt.patientName} — MDT Review`;
    if (mdt.title) return mdt.title;
    return `MDT Report #${mdt.id}`;
  };

  return (
    <div className="space-y-6">
      {/* ── Dashboard Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#10253A]">Super Admin Dashboard</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Comprehensive view of system operations, user registrations, and clinical reports.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => {
              fetchDoctors();
              fetchMdts();
              fetchActivity();
            }}
            disabled={isAnyLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-[#E4EAF0] bg-white px-3.5 py-2 text-xs font-semibold text-[#10253A] shadow-xs hover:bg-neutral-50 transition-colors disabled:opacity-60 cursor-pointer"
          >
            <RefreshCw size={13} className={isAnyLoading ? "animate-spin text-[#15BBAE]" : "text-[#15BBAE]"} />
            <span>Refresh All</span>
          </button>
        </div>
      </div>

      {/* ── Doctor Onboarding Overview & Trend Section ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Doctor Onboarding Overview */}
        <div className="card min-w-0">
          <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Stethoscope className="h-4 w-4 text-primary-500 shrink-0" aria-hidden="true" />
              <h3 className="text-[15px] font-semibold text-[#10253A] min-w-0 truncate">
                Doctor Onboarding Overview
              </h3>
            </div>
            <button
              onClick={() => navigate("/doctors")}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
            >
              View all
            </button>
          </div>

          {loadingDoctors && allDoctors.length === 0 ? (
            <OverviewCardSkeleton className="border-0 shadow-none p-0" />
          ) : errorDoctors && allDoctors.length === 0 ? (
            <WidgetErrorState
              title="Doctor Overview Unavailable"
              message={errorDoctors}
              onRetry={fetchDoctors}
              variant="card"
              className="border-0 shadow-none py-6"
            />
          ) : (
            <>
              {isStaleDoctors && (
                <div className="mb-4">
                  <StaleDataBanner
                    message="Failed to refresh latest doctor statistics. Showing cached data."
                    onRetry={fetchDoctors}
                  />
                </div>
              )}

              <div className="mb-4">
                <OverviewStatStrip
                  items={[
                    {
                      key: "total",
                      label: "Doctors",
                      value: totalDoctors,
                      color: "#15BBAE",
                      icon: <Stethoscope size={14} aria-hidden="true" />,
                    },
                    {
                      key: "completed",
                      label: "Completed",
                      value: onboardingCounts.completed,
                      color: "#15BBAE",
                      icon: <CheckCircle2 size={14} aria-hidden="true" />,
                    },
                    {
                      key: "inProgress",
                      label: "In Progress",
                      value: onboardingCounts.inProgress,
                      color: "#63788E",
                      icon: <Clock size={14} aria-hidden="true" />,
                    },
                    {
                      key: "notStarted",
                      label: "Not Started",
                      value: onboardingCounts.notStarted,
                      color: "#F45B78",
                      icon: <Ban size={14} aria-hidden="true" />,
                    },
                  ]}
                />
              </div>

              {totalDoctors === 0 ? (
                <EmptyState
                  icon={<Stethoscope size={24} />}
                  title="No doctors registered yet"
                  description="Add your first doctor to enable MDT reviews, patient assignments, and consultations."
                  action={{
                    label: "Add Doctor",
                    to: "/doctors/new",
                  }}
                  variant="compact"
                />
              ) : (
                <MDTDonut
                  segments={doctorSegments}
                  total={totalDoctors}
                  centerLabel="Doctors"
                  size={190}
                  thickness={28}
                />
              )}
            </>
          )}
        </div>

        {/* Doctor Onboarding Trend Chart */}
        <div className="card min-w-0">
          <OnboardingLineChart
            title="Doctor Onboarding"
            icon={<BarChart3 className="h-4 w-4" aria-hidden="true" />}
            dates={doctorDates}
            color="#15BBAE"
            loading={loadingDoctors}
            error={errorDoctors}
            onRetry={fetchDoctors}
            isStale={isStaleDoctors}
            emptyActionTo="/doctors/new"
            emptyActionLabel="Add Doctor"
          />
        </div>
      </div>

      {/* ── MDT Status Overview & Report Volume Section ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* MDT Status Overview */}
        <div className="card min-w-0">
          <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-primary-500 shrink-0" aria-hidden="true" />
              <h3 className="text-[15px] font-semibold text-[#10253A] min-w-0 truncate">
                MDT Status Overview
              </h3>
            </div>
            <button
              onClick={() => navigate("/reports")}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
            >
              View all
            </button>
          </div>

          {loadingMdts && allReports.length === 0 ? (
            <OverviewCardSkeleton className="border-0 shadow-none p-0" />
          ) : errorMdts && allReports.length === 0 ? (
            <WidgetErrorState
              title="MDT Overview Unavailable"
              message={errorMdts}
              onRetry={fetchMdts}
              variant="card"
              className="border-0 shadow-none py-6"
            />
          ) : (
            <>
              {isStaleMdts && (
                <div className="mb-4">
                  <StaleDataBanner
                    message="Failed to refresh latest MDT reports. Showing cached data."
                    onRetry={fetchMdts}
                  />
                </div>
              )}

              <div className="mb-4">
                <OverviewStatStrip
                  items={[
                    {
                      key: "total",
                      label: "Total Reports",
                      value: totalMdts,
                      color: "#15BBAE",
                      icon: <FileText size={14} aria-hidden="true" />,
                    },
                    {
                      key: "approved",
                      label: "Approved",
                      value: donutData.approved,
                      color: "#15BBAE",
                      icon: <CheckCircle2 size={14} aria-hidden="true" />,
                    },
                    {
                      key: "pending",
                      label: "Pending",
                      value: donutData.pending,
                      color: "#63788E",
                      icon: <Clock size={14} aria-hidden="true" />,
                    },
                    {
                      key: "rejected",
                      label: "Rejected",
                      value: donutData.rejected,
                      color: "#F45B78",
                      icon: <Ban size={14} aria-hidden="true" />,
                    },
                  ]}
                />
              </div>

              {totalMdts === 0 ? (
                <EmptyState
                  icon={<FileText size={24} />}
                  title="No MDT reports recorded yet"
                  description="No MDT reports or cases have been created yet."
                  action={{
                    label: "View All Reports",
                    to: "/reports",
                  }}
                  variant="compact"
                />
              ) : (
                <MDTDonut
                  segments={donutSegments}
                  total={totalMdts}
                  centerLabel="MDT Reports"
                  size={190}
                  thickness={28}
                />
              )}
            </>
          )}
        </div>

        {/* MDT Report Volume Chart */}
        <div className="card min-w-0">
          <OnboardingLineChart
            title="MDT Report Volume"
            icon={<BarChart3 className="h-4 w-4" aria-hidden="true" />}
            dates={mdtDates}
            color="#0E5A5A"
            loading={loadingMdts}
            error={errorMdts}
            onRetry={fetchMdts}
            isStale={isStaleMdts}
            emptyActionTo="/reports"
            emptyActionLabel="View Reports"
          />
        </div>
      </div>

      {/* ── Recent Activity & MDT Submitted Section ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ================= Recent Activity ================= */}
        <div className="card min-w-0">
          <div className="flex items-center justify-between gap-2 mb-4 min-w-0">
            <h3 className="text-[15px] font-semibold text-[#10253A] min-w-0 truncate">
              Recent Activity
            </h3>
            <button
              onClick={() => navigate("/activity")}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
            >
              View all
            </button>
          </div>

          {loadingActivity && recentActivity.length === 0 ? (
            <TableSkeleton rows={4} columns={2} className="border-0 shadow-none p-0" />
          ) : errorActivity && recentActivity.length === 0 ? (
            <WidgetErrorState
              title="Recent Activity Unavailable"
              message={errorActivity}
              onRetry={fetchActivity}
              variant="card"
              className="border-0 shadow-none"
            />
          ) : recentActivity.length === 0 ? (
            <EmptyState
              icon={<Users size={22} />}
              title="No recent activity"
              description="No recent activity logs or events recorded yet."
              variant="compact"
            />
          ) : (
            <div className="space-y-3 min-w-0">
              {isStaleActivity && (
                <div className="mb-3">
                  <StaleDataBanner
                    message="Failed to refresh recent activity. Showing cached data."
                    onRetry={fetchActivity}
                  />
                </div>
              )}
              {recentActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg min-w-0 overflow-hidden"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.type === "patient"
                        ? "bg-primary-100"
                        : activity.type === "report"
                        ? "bg-primary-100"
                        : activity.type === "note"
                        ? "bg-success-50"
                        : activity.type === "doctor-unblocked"
                        ? "bg-success-50"
                        : activity.type === "doctor-blocked"
                        ? "bg-danger-100"
                        : "bg-neutral-100"
                    }`}
                  >
                    {activity.type === "patient" && <Users className="h-4 w-4 text-primary-600" />}
                    {activity.type === "report" && <FileText className="h-4 w-4 text-primary-600" />}
                    {activity.type === "note" && <FileText className="h-4 w-4 text-success-500" />}
                    {activity.type === "doctor-unblocked" && <UserPlus className="h-4 w-4 text-success-600" />}
                    {activity.type === "doctor-blocked" && <UserRoundX className="h-4 w-4 text-danger-600" />}
                    {activity.type === "user" && <UserPlus className="h-4 w-4 text-neutral-600" />}
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="font-medium text-neutral-900 line-clamp-2 break-words">{activity.action}</p>
                    {activity.subtitle ? (
                      <p className="text-sm text-neutral-600 line-clamp-2 break-words mt-0.5">{activity.subtitle}</p>
                    ) : null}
                    {activity.createdAt ? (
                      <p className="text-xs text-neutral-500 mt-1">{timeAgo(activity.createdAt)}</p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ================= MDT Submitted ================= */}
        <div className="card min-w-0">
          <div className="flex items-center justify-between gap-2 mb-4 min-w-0">
            <h3 className="text-[15px] font-semibold text-[#10253A] min-w-0 truncate">
              MDT Submitted
            </h3>
            <button
              onClick={() => navigate("/reports")}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
            >
              View all
            </button>
          </div>

          {loadingMdts && submittedMdts.length === 0 ? (
            <TableSkeleton rows={4} columns={2} className="border-0 shadow-none p-0" />
          ) : errorMdts && submittedMdts.length === 0 ? (
            <WidgetErrorState
              title="MDT Submissions Unavailable"
              message={errorMdts}
              onRetry={fetchMdts}
              variant="card"
              className="border-0 shadow-none"
            />
          ) : submittedMdts.length === 0 ? (
            <EmptyState
              icon={<FileText size={22} />}
              title="No pending MDT submissions"
              description="All cases have been reviewed or none submitted yet."
              action={{
                label: "View All Reports",
                to: "/reports",
              }}
              variant="compact"
            />
          ) : (
            <div className="space-y-3 min-w-0">
              {submittedMdts.map((mdt) => (
                <div
                  key={mdt.id}
                  className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg min-w-0 overflow-hidden cursor-pointer hover:bg-neutral-100 transition-colors"
                  onClick={() => navigate(`/reports/${mdt.id}`)}
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-4 w-4 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-neutral-900 line-clamp-2 break-words">{mdtTitle(mdt)}</p>
                      {["approved", "final"].includes(mdt.status) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800 flex-shrink-0">
                          Approved
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-600 line-clamp-2 break-words mt-0.5">
                      Submitted by <strong>{mdt.created_by?.name || mdt.title || "Junior Doctor"}</strong>
                    </p>
                    {mdt.reviewed_by?.name ? (
                      <p className="text-sm text-neutral-600 line-clamp-2 break-words mt-0.5">
                        Submitted to <strong>{mdt.reviewed_by.name}</strong>
                      </p>
                    ) : null}
                    <p className="text-xs text-neutral-500 mt-1">
                      {timeAgo(mdt.updatedAt ?? mdt.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
